import { getApp, getApps, initializeApp } from 'firebase/app';
import {
  applyActionCode,
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  deleteUser,
  EmailAuthProvider,
  getAuth,
  initializeAuth,
  inMemoryPersistence,
  PhoneAuthProvider,
  reauthenticateWithCredential,
  RecaptchaVerifier,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPhoneNumber,
  signOut,
  updatePhoneNumber,
  verifyBeforeUpdateEmail,
  verifyPasswordResetCode,
  type Auth,
  type User,
} from 'firebase/auth';
import type {
  AuthUser,
  FirebaseAuthService,
  FirebaseWebConfig,
  PhoneIntent,
  SentPhoneCode,
} from './types';

export function toAuthUser(user: User | null): AuthUser | null {
  if (!user) return null;
  return {
    uid: user.uid,
    email: user.email,
    phoneNumber: user.phoneNumber,
    emailVerified: user.emailVerified,
    providerIds: user.providerData.map((p) => p.providerId),
  };
}

export function noCurrentUser(): Error {
  return Object.assign(new Error('No signed-in Firebase user'), { code: 'auth/no-current-user' });
}

/** `memory` keeps no signed-in user, for use beside native auth. */
export function createJsAuth(
  config: FirebaseWebConfig,
  persistence: 'browser' | 'memory'
): () => Auth {
  let auth: Auth | null = null;
  return () => {
    if (!auth) {
      const app = getApps().length ? getApp() : initializeApp(config);
      auth =
        persistence === 'memory'
          ? initializeAuth(app, { persistence: inMemoryPersistence })
          : getAuth(app);
    }
    return auth;
  };
}

export class WebFirebaseAuth implements FirebaseAuthService {
  readonly platform = 'web' as const;
  private readonly auth: () => Auth;

  constructor(config: FirebaseWebConfig) {
    this.auth = createJsAuth(config, 'browser');
  }

  private async user(): Promise<User> {
    const auth = this.auth();
    await auth.authStateReady();
    if (!auth.currentUser) throw noCurrentUser();
    return auth.currentUser;
  }

  async currentUser() {
    const auth = this.auth();
    await auth.authStateReady();
    return toAuthUser(auth.currentUser);
  }

  async reload() {
    const auth = this.auth();
    await auth.authStateReady();
    await auth.currentUser?.reload();
    return toAuthUser(auth.currentUser);
  }

  async getIdToken(forceRefresh = false) {
    return (await this.user()).getIdToken(forceRefresh);
  }

  signOut() {
    return signOut(this.auth());
  }

  async deleteCurrentUser() {
    await deleteUser(await this.user());
  }

  async signInWithEmail(email: string, password: string) {
    return toAuthUser((await signInWithEmailAndPassword(this.auth(), email, password)).user)!;
  }

  async createUserWithEmail(email: string, password: string) {
    return toAuthUser((await createUserWithEmailAndPassword(this.auth(), email, password)).user)!;
  }

  async sendEmailVerification() {
    await sendEmailVerification(await this.user());
  }

  sendPasswordResetEmail(email: string) {
    return sendPasswordResetEmail(this.auth(), email);
  }

  verifyPasswordResetCode(oobCode: string) {
    return verifyPasswordResetCode(this.auth(), oobCode);
  }

  confirmPasswordReset(oobCode: string, newPassword: string) {
    return confirmPasswordReset(this.auth(), oobCode, newPassword);
  }

  applyActionCode(oobCode: string) {
    return applyActionCode(this.auth(), oobCode);
  }

  async verifyBeforeUpdateEmail(newEmail: string, continueUrl: string) {
    await verifyBeforeUpdateEmail(await this.user(), newEmail, {
      url: continueUrl,
      handleCodeInApp: true,
    });
  }

  async reauthenticateWithPassword(password: string) {
    const user = await this.user();
    if (!user.email) throw Object.assign(new Error('No email'), { code: 'auth/no-such-provider' });
    await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, password));
  }

  async sendPhoneCode(phoneNumber: string, intent: PhoneIntent): Promise<SentPhoneCode> {
    const auth = this.auth();
    // A throwaway host per send, so callers never manage reCAPTCHA elements.
    const host = document.createElement('div');
    document.body.append(host);
    const verifier = new RecaptchaVerifier(auth, host, { size: 'invisible' });
    try {
      if (intent === 'signIn') {
        const confirmation = await signInWithPhoneNumber(auth, phoneNumber, verifier);
        return {
          verifiedUser: null,
          confirm: async (code) => toAuthUser((await confirmation.confirm(code)).user)!,
        };
      }
      const verificationId = await new PhoneAuthProvider(auth).verifyPhoneNumber(
        phoneNumber,
        verifier
      );
      return {
        verifiedUser: null,
        confirm: async (code) => {
          const user = await this.user();
          const credential = PhoneAuthProvider.credential(verificationId, code);
          if (intent === 'reauthenticate') await reauthenticateWithCredential(user, credential);
          else await updatePhoneNumber(user, credential);
          return toAuthUser(user)!;
        },
      };
    } finally {
      verifier.clear();
      host.remove();
    }
  }
}
